import { useNavigate } from '../../../context/navigation-context';
import { cn } from '../../../lib/styles';
import { Button } from '../button/button.spark';
import { ExternalLink } from '../external-link/external-link.spark';
import { Heading } from '../heading/heading.spark';
import { Icon } from '../icon/icon.spark';
import { LearnMoreLink } from '../learn-more-link/learn-more-link.spark';
import { Text } from '../text/text.spark';
import { SecondaryActionsMenu, singleSecondaryAction } from './page.shared';
import type { PageHeaderProps, PageProps } from './types';

export function Page(props: PageProps) {
  return (
    <div
      className={cn(
        'mx-auto flex flex-col gap-4',
        props.fillHeight && 'min-h-0 flex-1',
        props.hasSideBar
          ? props.narrow
            ? 'w-full max-w-2xl xl:w-fit xl:max-w-none'
            : 'w-fit'
          : props.narrow
            ? 'w-full max-w-2xl'
            : 'w-full max-w-7xl',
      )}
    >
      {props.title && <PageHeader {...props} />}
      <div
        className={cn(
          props.fillHeight && 'min-h-0 flex-1',
          props.hasSideBar ? 'gap-5 xl:grid xl:grid-cols-[1fr_15rem]' : 'flex flex-col gap-5',
        )}
      >
        {props.children}
      </div>
      <div className="mt-4 w-full text-center">
        <ExternalLink link={props.learnMoreUrl}>{props.footerDescription}</ExternalLink>
      </div>
    </div>
  );
}

function PageHeader({
  title,
  description,
  learnMoreUrl,
  backUrl,
  backUrlOpts,
  secondaryActions,
  primaryAction,
}: PageHeaderProps) {
  const navigate = useNavigate();
  const soleSecondaryAction = singleSecondaryAction(secondaryActions);
  return (
    <header className="flex shrink-0 flex-col gap-2">
      <span className="flex items-center justify-between">
        <span className="flex flex-nowrap items-center gap-1">
          {backUrl && (
            <button
              type="button"
              // biome-ignore lint/style/noNonNullAssertion: backUrl should always be defined here, but type checker doesn't know that
              onClick={() => navigate(backUrl!, backUrlOpts)}
              className="h-min cursor-pointer rounded-md p-1 transition hover:bg-spark-surface-hover-strong"
            >
              <Icon name="back" size="lg" />
            </button>
          )}
          <Heading level="h1" style="xl">
            {title}
          </Heading>
        </span>
        <span className="inline-flex gap-2">
          {soleSecondaryAction && (
            <span className={cn({ 'hidden sm:contents': soleSecondaryAction.hideOnMobile })}>
              <Button
                key={soleSecondaryAction.label}
                disabled={soleSecondaryAction.disabled}
                loading={soleSecondaryAction.loading}
                icon={soleSecondaryAction.icon}
                variant={soleSecondaryAction.danger ? 'danger' : 'secondary'}
                onClick={soleSecondaryAction.onClick}
              >
                {soleSecondaryAction.label}
              </Button>
            </span>
          )}

          <SecondaryActionsMenu actions={secondaryActions} />

          {primaryAction && (
            <Button
              key={primaryAction.label}
              icon={primaryAction.icon}
              variant="primary"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              loading={primaryAction.loading}
              collapseLabel="mobile"
            >
              {primaryAction.label}
            </Button>
          )}
        </span>
      </span>
      {description && (
        <Text variant="secondary">
          {description} <LearnMoreLink link={learnMoreUrl} />
        </Text>
      )}
    </header>
  );
}
